import { useId, useRef, useState, type ReactElement } from 'react';
import { Box, BrandLockup, CheckIcon, Menu, MenuItem, Text } from '@citibox-panel/ui';
import type { MarketIndicator } from '@/lib/marketIndicator';
import { SPACES, type SpaceId } from '@/lib/spaces';

export interface SpaceSelectorProps {
  current: SpaceId | null;
  usable: readonly SpaceId[];
  indicator: MarketIndicator;
  spaceLabel: string;
  onSelect: (spaceId: SpaceId) => void;
}

// TODO: i18n
const COPY = {
  menu: 'Espacios del panel',
  change: 'cambiar de espacio',
  unverified: 'mercado sin verificar',
} as const;

export function SpaceSelector({ current, usable, indicator, spaceLabel, onSelect }: SpaceSelectorProps): ReactElement {
  const anchorRef = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [initialFocus, setInitialFocus] = useState<'first' | 'last'>('first');
  const menuId = useId();

  // A menu whose only option is where the person already stands is noise: the pill stays as today.
  if (!usable.some((id) => id !== current)) {
    return <BrandLockup label={indicator.text} degraded={indicator.degraded} />;
  }

  // Starts with the visible text (WCAG 2.5.3); a bare «—» would be read out as a dash.
  const accessibleName = indicator.degraded
    ? `${spaceLabel}, ${COPY.unverified}, ${COPY.change}`
    : `${indicator.text}, ${COPY.change}`;
  const close = (): void => {
    setOpen(false);
  };

  return (
    <>
      <BrandLockup
        ref={anchorRef}
        label={indicator.text}
        degraded={indicator.degraded}
        selector={{
          expanded: open,
          controls: menuId,
          onOpen: (focus) => {
            setInitialFocus(focus);
            setOpen(true);
          },
          accessibleName,
        }}
      />
      <SpaceMenu
        id={menuId}
        anchorEl={anchorRef.current}
        open={open}
        onClose={close}
        initialFocus={initialFocus}
        current={current}
        usable={usable}
        onSelect={onSelect}
      />
    </>
  );
}

interface SpaceMenuProps {
  id: string;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  initialFocus: 'first' | 'last';
  current: SpaceId | null;
  usable: readonly SpaceId[];
  onSelect: (spaceId: SpaceId) => void;
}

function SpaceMenu({ id, anchorEl, open, onClose, initialFocus, current, usable, onSelect }: SpaceMenuProps): ReactElement {
  return (
    <Menu id={id} aria-label={COPY.menu} anchorEl={anchorEl} open={open} onClose={onClose} initialFocus={initialFocus}>
      {SPACES.filter((space) => usable.includes(space.id)).map((space) => {
        const isCurrent = space.id === current;
        return (
          <MenuItem
            key={space.id}
            role="menuitemradio"
            aria-checked={isCurrent}
            selected={isCurrent}
            onClick={() => {
              onClose();
              // Re-picking the current space must not navigate: that would drop the open search tabs.
              if (!isCurrent) onSelect(space.id);
            }}
          >
            <Text color={isCurrent ? 'brand' : 'primary'}>{space.label}</Text>
            {isCurrent && (
              // Decorative echo of aria-checked, trailing the row like the panel's other pickers.
              <Box sx={{ display: 'flex', ml: 'auto', pl: 2 }}>
                <CheckIcon fontSize="small" />
              </Box>
            )}
          </MenuItem>
        );
      })}
    </Menu>
  );
}
